import { Alert, Grid, Box } from '@vapor/material'
import { ConditionalWrapper, FormItem } from 'hic-components'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FiscalType, useCashDesksQuery } from 'src/api/api.generated'
import { displayFiscalType } from 'src/utils/fiscal-type'
import { CashDeskTypeOption } from './cash-desk.types'
import { isCashDeskDisabled, isResourceTypesSubset } from './cash-desk-utils'
import CashDeskInput from './cash-desk-input'

export type CashDeskSimpleSelectorProps = {
  value: string | null
  tenantId: string
  propertyId: string
  fiscalType: FiscalType | null | undefined
  /** Are, the resource types referenced by production assignments.
   * Helps to detemine the correct cash desk.
   * Only if there are different fiscal configuration associated to resourceTypes
   */
  documentResourceTypes: string[]
  onCashDeskIdChanged: (cashDeskId: string | null) => void
  alertWrapper?: (children: React.ReactNode) => React.ReactNode
  error?: string
  loading?: boolean
  disabled?: boolean
}

export function CashDeskSimpleSelector({ value, tenantId, propertyId, fiscalType, documentResourceTypes, onCashDeskIdChanged, alertWrapper, error, loading, disabled }: CashDeskSimpleSelectorProps) {
  const { t } = useTranslation()

  const { data } = useCashDesksQuery({
    variables: {
      tenantId,
      propertyId,
      cashDeskId: value && value !== null && disabled ? { eq: value } : undefined,
      open: !value || value === null || !disabled ? true : undefined,
    },
  })

  const multipleFiscalConfigurations = useMemo(() => {
    if (data?.properties) {
      const fc = data.properties?.[0]?.fiscalConfigurations ?? []
      if (fc.length) {
        if (fc.length > 1) return true
        else return (fc[0].cashDeskTypeIds.length ?? 0) > 1
      }
    }
    return false
  }, [data])

  /** Are the cash desks that support current fiscal type */
  const cashDesks: CashDeskTypeOption[] | null = useMemo(() => {
    if (fiscalType === null || fiscalType === undefined) {
      return null
    }
    const result: CashDeskTypeOption[] =
      data?.properties?.[0]?.fiscalConfigurations?.map<CashDeskTypeOption>((fc) => ({
        cashFiscalConfiguration: {
          name: fc.name,
          resourceTypeIds: fc.resourceTypeIds,
        },
        cashes: multipleFiscalConfigurations ? data.cashDesks?.filter((c) => fc.cashDeskTypeIds.includes(c.cashDeskType.id)) ?? [] : [...(data.cashDesks ?? [])],
      })) ?? []
    console.log('cashDesks', result)
    return result
  }, [fiscalType, data, multipleFiscalConfigurations])

  /** The cashDeskId selected by user or suggested */
  const cashDeskId = useMemo(() => {
    let result: string | undefined = undefined
    if (value) {
      result = value
    } else {
      const compatibleFiscalConfig = multipleFiscalConfigurations
        ? cashDesks?.find((cd) => cd.cashFiscalConfiguration.resourceTypeIds && isResourceTypesSubset(documentResourceTypes, cd.cashFiscalConfiguration.resourceTypeIds))
        : cashDesks?.[0]
      if (fiscalType && compatibleFiscalConfig && compatibleFiscalConfig.cashes.length > 0) {
        result = compatibleFiscalConfig.cashes.find((c) => !isCashDeskDisabled(c, fiscalType))?.id ?? undefined
      }
    }
    console.log('CASH SELECTED', result)
    return result
  }, [fiscalType, documentResourceTypes, cashDesks, value, multipleFiscalConfigurations])

  /** Check if selected cash desk is compatible with document resource types */
  const selectedCashDeskNotCompatible = useMemo(() => {
    if (value && documentResourceTypes.length > 0) {
      if (multipleFiscalConfigurations) {
        const selectedFiscalConfiguration = cashDesks?.find((c) => c.cashes.map((i) => i.id).includes(value))?.cashFiscalConfiguration
        if (selectedFiscalConfiguration) return !isResourceTypesSubset(documentResourceTypes, selectedFiscalConfiguration.resourceTypeIds ?? [])
      } else {
        return true
      }
    }
    return false
  }, [value, cashDesks, documentResourceTypes, multipleFiscalConfigurations])

  useEffect(() => {
    onCashDeskIdChanged(cashDeskId ?? null)
  }, [cashDeskId])

  const uniqueCashDeskIds = useMemo(() => {
    if (fiscalType)
      return cashDesks?.reduce((uniqueIdList: string[], current) => {
        const copyOfcurrentList = [...uniqueIdList]
        current.cashes
          .filter((cc) => !isCashDeskDisabled(cc, fiscalType))
          .forEach((c) => {
            if (!copyOfcurrentList.includes(c.id)) copyOfcurrentList.push(c.id)
          })
        return [...copyOfcurrentList]
      }, [])
  }, [cashDesks, fiscalType])

  const showCashDesk = useMemo(() => {
    return (uniqueCashDeskIds ?? []).length > 1 || (value && multipleFiscalConfigurations)
  }, [uniqueCashDeskIds, value, multipleFiscalConfigurations])

  if (!cashDeskId && fiscalType && uniqueCashDeskIds?.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
        <ConditionalWrapper<boolean> //
          condition={alertWrapper !== undefined}
          wrapper={(condition, children) => (condition ? <>{alertWrapper?.(children)}</> : <>{children}</>)}
        >
          <Alert severity="error">{t('pms.cash_desk.error.unavailable', { fiscalType: displayFiscalType(t, fiscalType) })}</Alert>
        </ConditionalWrapper>
      </Box>
    )
  } else {
    if (fiscalType && cashDesks && showCashDesk)
      return (
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <FormItem //
              fullWidth
              fieldLabel={t('pms.accounting.document.cash_desk')}
              errorMessage={error}
            >
              <CashDeskInput
                cashDesksData={cashDesks}
                value={cashDeskId ?? value}
                onChange={onCashDeskIdChanged}
                fiscalType={fiscalType}
                error={error ? true : false}
                loading={loading}
                disabled={disabled}
              />
            </FormItem>
          </Grid>
          <Grid item xs={8}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
              {(!cashDeskId || selectedCashDeskNotCompatible) && (
                <Alert variant="standard" severity="warning">
                  {t('pms.cash_desk.warning.not_fully_compatible')}
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      )

    return <></>
  }
}
